import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogIn,
  ChevronDown,
  LogOut,
  ListChecks,
  Building2,
  Film,
  HelpCircle,
  Settings as SettingsIcon,
} from 'lucide-react';
import { useAuth } from './useAuth';
import SignInModal from './SignInModal';

interface HeaderAuthProps {
  isLightHeader: boolean;
  /** Active station slug from the URL (closed loop). When absent the
   *  My Campaigns menu item hides — there is no global /my-campaigns. */
  slug?: string | null;
}

const HeaderAuth: React.FC<HeaderAuthProps> = ({ isLightHeader, slug }) => {
  const {
    user,
    isAuthenticated,
    signOut,
    advertiserMemberships,
    selectedAdvertiser,
    setSelectedAdvertiserId,
  } = useAuth();
  const navigate = useNavigate();
  const [signInOpen, setSignInOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  // Click-outside detection via document listener — avoids the z-index war
  // a `fixed inset-0` clickaway creates against the header's stacking context
  // (header has backdrop-blur → its own containing block).
  React.useEffect(() => {
    if (!menuOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [menuOpen]);

  const buttonClass = isLightHeader
    ? 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'
    : 'text-white/90 hover:text-white hover:bg-white/10';

  // Seat redesign: display the currently-selected advertiser's name when the
  // user has at least one. Fall back to the email prefix for users with no
  // memberships yet (publisher staff, fresh anon-becoming-authed users mid-
  // wizard before ensure-self has fired).
  const displayName = selectedAdvertiser?.advertiserName || (user?.email ?? '').split('@')[0];
  const hasMultipleAdvertisers = (advertiserMemberships?.length ?? 0) >= 2;
  const hasAnyAdvertiser = (advertiserMemberships?.length ?? 0) >= 1;
  const signedOut = !isAuthenticated || !user;

  // Single wrapper for both signed-in and signed-out states so React
  // preserves the SignInModal instance through the auth flip that fires the
  // moment `verifyOtp` resolves. If the modal moves parents (fragment ↔ div)
  // its internal `stage` state resets to 'email-password' mid-flow, and the
  // post-OTP set-password nudge disappears (CTV-591).
  return (
    <div ref={wrapperRef} className="relative shrink-0">
      {signedOut ? (
        <button
          type="button"
          onClick={() => setSignInOpen(true)}
          className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${buttonClass}`}
        >
          <LogIn className="w-4 h-4" />
          <span className="hidden sm:inline">Sign in</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${buttonClass}`}
        >
          <span className="max-w-[140px] truncate">Hi, {displayName}</span>
          <ChevronDown className="w-4 h-4" />
        </button>
      )}
      <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />

      {!signedOut && menuOpen && user && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg shadow-xl border border-slate-200 z-50 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <div className="text-xs text-slate-500">Signed in as</div>
              <div className="text-sm font-medium text-slate-900 truncate">{user.email}</div>
            </div>

            {/* Multi-advertiser switcher: only shown when the user belongs to
                ≥2 seats. Single-seat users just see the current advertiser as
                a static label below their email. */}
            {hasMultipleAdvertisers && advertiserMemberships && (
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="text-xs text-slate-500 mb-1">Current advertiser</div>
                <select
                  value={selectedAdvertiser?.advertiserId ?? ''}
                  onChange={(e) => {
                    setSelectedAdvertiserId(e.target.value);
                    setMenuOpen(false);
                  }}
                  className="w-full text-sm bg-white border border-slate-300 rounded-md py-1.5 px-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
                >
                  {advertiserMemberships.map((m) => (
                    <option key={m.advertiserId} value={m.advertiserId}>
                      {m.advertiserName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {!hasMultipleAdvertisers && selectedAdvertiser && (
              <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-2 text-sm text-slate-700">
                <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
                <span className="truncate">{selectedAdvertiser.advertiserName}</span>
              </div>
            )}

            {slug && (
              <a
                href={`/${slug}/my-campaigns`}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
              >
                <ListChecks className="w-4 h-4" />
                My Campaigns
              </a>
            )}
            {/* CTV-457 Phase B: cross-station library of commercials the
                user has submitted. Lives behind a slug so PublicHeader picks
                up a station context, but the data itself is per-user. */}
            {slug && (
              <a
                href={`/${slug}/library`}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 border-t border-slate-100"
              >
                <Film className="w-4 h-4" />
                My Library
              </a>
            )}
            {slug && (
              <a
                href={`/${slug}/help`}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 border-t border-slate-100"
              >
                <HelpCircle className="w-4 h-4" />
                Help Center
              </a>
            )}
            {/* Consolidated Settings surface, business + team + security in
                one place. Lands on the Business tab by default; the tab strip
                on the page lets the user swap. Team + Business tabs both
                require an advertiser seat, but Security works without one,
                so we surface the link whenever there's a slug. */}
            {slug && (
              <a
                href={`/${slug}/settings/${hasAnyAdvertiser ? 'business' : 'security'}`}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 border-t border-slate-100"
              >
                <SettingsIcon className="w-4 h-4" />
                Settings
              </a>
            )}
            <button
              type="button"
              onClick={async () => {
                setMenuOpen(false);
                await signOut();
                // Closed-loop sign-out: stay inside the station context when
                // we have one (so the user lands on the station's public
                // landing page, not the global marketplace). Auth-gated pages
                // like /:slug/my-campaigns would re-pop the sign-in modal, so
                // we route to /:slug specifically. Without a slug, fall back
                // to the marketplace root.
                navigate(slug ? `/${slug}` : '/', { replace: true });
              }}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 text-left border-t border-slate-100"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
        </div>
      )}
    </div>
  );
};

export default HeaderAuth;
